// /onboarding/done — «نشاطك جاهز» (board 25 v2 done--default): the
// celebration in order — the mark becomes a check, twelve coral dots burst
// and fade, then the coral button appears (1.9 s, CSS `onb-delayed`) so two
// corals never share the screen (rule 2). The business was written by the
// hours step; here erp-core is asked once, best effort, to mirror profile's
// branches (GET /erp/branches?refresh=1 — its provisioning gives the branch
// its main till «الصندوق الرئيسي» and main warehouse; ruling R19). The panel shows the finished preview
// with «منشورة» and the link; on a phone the preview sits between the hero
// and the two buttons. «افتح اليوم» → /today · «استيراد من المربع» →
// /customers/import — both make the new business the active one and clear
// the wizard. State: default.

import { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileInput } from 'lucide-react';
import { BrandMark } from '@/components/BrandMark';
import { Button, PrimaryAction } from '@/components/Button';
import { sessionStore } from '@/lib/auth';
import { useLayout } from '@/lib/layout';
import { LivePreview } from '../components/LivePreview';
import { CelebrationBurst } from '../components/widgets';
import { Wizard } from '../components/Wizard';
import { DONE } from '../copy';
import { erpRefresh } from '../data/api';
import { unscheduledRoute } from '../data/enroll';
import { ONB } from '../data/flow';
import { onboardingStore, useOnboarding } from '../state';
import { useBusinessPreview } from './Business';

export function DoneScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobile = useLayout() === 'phone';
  const { createdId, scheduled, shapeDone, erpSynced, invites } = useOnboarding();
  const authed = sessionStore.isAuthenticated();
  const { profile, preview } = useBusinessPreview('done', invites);
  const syncAsked = useRef(false);
  /** Set once the person leaves: this screen renders nothing more (the store is reset under it). */
  const finished = useRef(false);

  // The erp-core mirror, once, best effort: its first ERP read mirrors and provisions lazily anyway.
  useEffect(() => {
    if (!authed || !createdId || !scheduled || !shapeDone || erpSynced || syncAsked.current) return;
    syncAsked.current = true;
    const id = createdId;
    erpRefresh(id)
      .catch(() => {
        /* best effort — erp-core mirrors the branches on its first read */
      })
      .finally(() => {
        if (onboardingStore.get().createdId === id) onboardingStore.set({ erpSynced: true });
      });
  }, [authed, createdId, scheduled, shapeDone, erpSynced]);

  if (finished.current) return null;
  if (!authed) return <Navigate to={ONB.phone} replace />;
  if (!createdId) return <Navigate to={ONB.hours} replace />;
  const pending = unscheduledRoute();
  if (pending) return <Navigate to={pending} replace />;
  // R8: the shape step's minimum first (Back / a deep link never skips it).
  if (!shapeDone) return <Navigate to={ONB.shape} replace />;

  const leave = (to: string) => {
    finished.current = true;
    sessionStore.setBusinessId(createdId);
    void qc.invalidateQueries();
    navigate(to, { replace: true });
    onboardingStore.reset();
  };

  return (
    <Wizard
      screen="done"
      testId="onb-done"
      state="default"
      centered
      primary={
        <PrimaryAction type="submit" size="lg" className="onb-delayed" icon={ArrowLeft} block={mobile} data-testid="onb-open-today">
          {DONE.openToday}
        </PrimaryAction>
      }
      secondary={
        <Button variant="line" size="lg" icon={FileInput} block={mobile} data-testid="onb-import" onClick={() => leave('/customers/import')}>
          {DONE.import}
        </Button>
      }
      onSubmit={() => leave('/today')}
      panel={{ phase: 'preview', mark: 'success', done: true, profile, preview }}
      mobileMark="static"
    >
      <div className="onb-hero">
        <div className="onb-hero-mark">
          <BrandMark mode="success" size={mobile ? 80 : 96} label="احجزلي" />
          <CelebrationBurst radius={mobile ? 60 : 80} />
        </div>
        <div className="onb-q">
          <h1 className="onb-title">{DONE.title}</h1>
          <p className="onb-sub">{mobile ? DONE.subMobile(profile) : DONE.sub(profile)}</p>
        </div>
      </div>
      {mobile && <LivePreview data={{ ...preview, still: true }} done width={350} cover={110} />}
    </Wizard>
  );
}
